export const SmokingStageType=Object.freeze({
  DRYING:'drying',
  WARMING:'warming',
  SMOKING:'smoking',
  BAKING:'baking',
  STEAMING:'steaming',
  RESTING:'resting',
  COOLING:'cooling',
  CUSTOM:'custom'
});

export const SMOKING_STAGE_TYPES=Object.freeze(Object.values(SmokingStageType));
const STAGE_TYPES=new Set(SMOKING_STAGE_TYPES);
const LABELS=Object.freeze({
  pl:Object.freeze({
    drying:'Osuszanie',
    warming:'Rozgrzewanie',
    smoking:'Wędzenie',
    baking:'Podpiekanie',
    steaming:'Parzenie / para',
    resting:'Odpoczynek',
    cooling:'Chłodzenie',
    custom:'Etap własny'
  }),
  en:Object.freeze({
    drying:'Drying',
    warming:'Warming',
    smoking:'Smoking',
    baking:'Baking',
    steaming:'Steaming',
    resting:'Resting',
    cooling:'Cooling',
    custom:'Custom stage'
  })
});

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function typeKey(value){
  const key=String(value||'').trim().toLowerCase();
  if(!STAGE_TYPES.has(key))throw new TypeError('Unsupported smoking stage type');
  return key;
}

function optionalName(value){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>160)throw new TypeError('nameOverride is too long');
  return text;
}

function optionalInstructions(value){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>8000)throw new TypeError('instructions is too long');
  return text;
}

function order(value){
  const number=value==null||value===''?0:Number(value);
  if(!Number.isInteger(number)||number<0||number>1000000){
    throw new TypeError('sortOrder must be an integer between 0 and 1000000');
  }
  return number;
}

function timestamp(value,label){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(Number.isNaN(Date.parse(text)))throw new TypeError(label+' must be an ISO timestamp');
  return text;
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function localeKey(locale){
  return String(locale||'pl').toLowerCase().startsWith('en')?'en':'pl';
}

export function stageTypeOptions({locale='pl'}={}){
  const lang=localeKey(locale);
  return Object.freeze(SMOKING_STAGE_TYPES.map(key=>Object.freeze({
    key,
    label:LABELS[lang][key]
  })));
}

export function stageDisplayName(stage,{locale='pl'}={}){
  const value=createSmokingStage(stage);
  if(value.nameOverride)return value.nameOverride;
  return LABELS[localeKey(locale)][value.typeKey];
}

export function createSmokingStage({
  id,
  userId,
  sessionId,
  typeKey:stageType,
  nameOverride=null,
  instructions=null,
  sortOrder=0,
  deletedAt=null
}={}){
  const type=typeKey(stageType);
  const name=optionalName(nameOverride);
  if(type===SmokingStageType.CUSTOM&&!name){
    throw new TypeError('nameOverride is required for a custom stage');
  }
  return Object.freeze({
    id:idOrGenerated(id),
    userId:required(userId,'userId',128),
    sessionId:required(sessionId,'sessionId',128),
    typeKey:type,
    nameOverride:name,
    instructions:optionalInstructions(instructions),
    sortOrder:order(sortOrder),
    deletedAt:timestamp(deletedAt,'deletedAt')
  });
}

export function updateSmokingStage(stage,changes={}){
  const current=createSmokingStage(stage);
  return createSmokingStage({
    ...current,
    ...changes,
    id:current.id,
    userId:current.userId,
    sessionId:current.sessionId
  });
}

export function removeSmokingStage(stage,{at=new Date().toISOString()}={}){
  return updateSmokingStage(stage,{deletedAt:timestamp(at,'at')});
}

export function orderSmokingStages(stages,{includeDeleted=false}={}){
  if(!Array.isArray(stages))throw new TypeError('stages must be an array');
  return Object.freeze(
    stages
      .map(createSmokingStage)
      .filter(stage=>includeDeleted||!stage.deletedAt)
      .sort((a,b)=>a.sortOrder-b.sortOrder||String(a.id).localeCompare(String(b.id)))
  );
}

export function smokingStageToRow(stage){
  const value=createSmokingStage(stage);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    session_id:value.sessionId,
    stage_type:value.typeKey,
    name_override:value.nameOverride,
    instructions:value.instructions,
    sort_order:value.sortOrder,
    deleted_at:value.deletedAt
  });
}

export function smokingStageFromRow(row={}){
  return createSmokingStage({
    id:row.id,
    userId:row.user_id,
    sessionId:row.session_id,
    typeKey:row.stage_type,
    nameOverride:row.name_override,
    instructions:row.instructions,
    sortOrder:row.sort_order,
    deletedAt:row.deleted_at
  });
}
